import { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { CHAT_SEARCH_MAX_QUERY_LENGTH, chatSearchMinQueryLength } from '@fias/platform-resources';
import {
  useChatMessageSearch,
  CHAT_SEARCH_DEFAULT_MATCH_MODE,
} from '../hooks/useChatMessageSearch';
import { NO_MATCH_HINT, searchTermsForHighlight } from './message-search-utils';
import { MessageSearchResultRow } from './MessageSearchResultRow';
import { SenderFilter } from './SenderFilter';
import { SearchMatchModeToggle } from './SearchMatchModeToggle';
import type { ChatChannelMemberResponse, ChatSearchMatchMode } from '@/lib/api';

interface ChannelSearchPanelProps {
  channelId: string | null;
  /** Name shown in the placeholder, e.g. "Search in #general". */
  channelLabel: string;
  /**
   * Channel members — suggestions for the "From" filter, not a limit on it. The
   * filter also accepts a typed handle, so senders past the loaded member page
   * and people who have left the channel stay reachable.
   */
  members?: ChatChannelMemberResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Scroll the conversation to this message. Optional — without it the results
   * are a read-only list (the embedded guild pane has no jump plumbing).
   */
  onSelectResult?: (messageId: string) => void;
}

/**
 * In-channel message search: text in message content, a "From" filter on
 * one sender, or both together. Either filter stands alone, so picking a sender
 * with an empty box lists everything they wrote here. Clicking a result closes
 * the panel and jumps to the message when it's inside the loaded window.
 */
export function ChannelSearchPanel({
  channelId,
  channelLabel,
  members,
  open,
  onOpenChange,
  onSelectResult,
}: ChannelSearchPanelProps) {
  const [rawQuery, setRawQuery] = useState('');
  const [senderFilter, setSenderFilter] = useState<string | null>(null);
  const [matchMode, setMatchMode] = useState<ChatSearchMatchMode>(CHAT_SEARCH_DEFAULT_MATCH_MODE);
  const query = useDebouncedValue(rawQuery, 250);

  // Start each visit from a clean slate rather than reopening onto a stale
  // result set from a previous channel.
  useEffect(() => {
    if (!open) {
      setRawQuery('');
      setSenderFilter(null);
    }
  }, [open]);

  const {
    results,
    total,
    isLoading,
    isError,
    isTooBroad,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useChatMessageSearch(channelId, query, senderFilter, open, matchMode);

  const trimmed = query.trim();
  const minLength = chatSearchMinQueryLength(matchMode);
  const hasTerms = trimmed.length >= minLength;
  const isQueryable = hasTerms || !!senderFilter;
  const isDebouncing = rawQuery.trim() !== trimmed;
  // Only the text terms get highlighted; a sender filter has nothing to mark.
  const terms = useMemo(
    () => searchTermsForHighlight(trimmed, hasTerms, matchMode),
    [trimmed, hasTerms, matchMode],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-primary flex-shrink-0" />
          <DialogTitle className="text-base font-semibold">Search messages</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Search this conversation by message text, by sender, or both.
        </DialogDescription>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <input
              type="search"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              maxLength={CHAT_SEARCH_MAX_QUERY_LENGTH}
              // The panel is only mounted-open on demand, so grabbing focus here
              // is what the user asked for by opening it.
              autoFocus
              placeholder={`Search in ${channelLabel}`}
              aria-label={`Search messages in ${channelLabel}`}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            {rawQuery.length > 0 && (
              <button
                type="button"
                onClick={() => setRawQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">From</span>
            <SenderFilter members={members ?? []} value={senderFilter} onChange={setSenderFilter} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">Match</span>
            <SearchMatchModeToggle value={matchMode} onChange={setMatchMode} />
          </div>
          {isQueryable && !isLoading && !isError && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {total} {total === 1 ? 'match' : 'matches'}
            </p>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto border-t border-border">
          {!isQueryable ? (
            <div className="text-center py-10 px-6">
              <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Type at least {minLength} characters to search
              </p>
              {matchMode === 'contains' && (
                <p className="text-xs text-muted-foreground/70 mt-1">
                  For shorter words, switch to “Whole word”.
                </p>
              )}
              <p className="text-xs text-muted-foreground/70 mt-1">
                Or pick someone under “From” to see everything they wrote here.
              </p>
            </div>
          ) : isLoading || isDebouncing ? (
            <p className="text-sm text-muted-foreground text-center py-10">Searching…</p>
          ) : isTooBroad ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-muted-foreground">That search matches too many messages</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Add more words, use a longer phrase, or pick a sender under “From”.
              </p>
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive text-center py-10">
              Couldn&apos;t run that search. Try again.
            </p>
          ) : results.length === 0 ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-muted-foreground">No messages found</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {hasTerms ? NO_MATCH_HINT[matchMode] : 'They haven’t posted in this conversation.'}
              </p>
            </div>
          ) : (
            <ul className="py-1">
              {results.map((message) => {
                const row = (
                  <MessageSearchResultRow
                    senderName={message.senderName || message.senderId}
                    content={message.content}
                    createdAt={message.createdAt}
                    terms={terms}
                  />
                );

                return (
                  <li key={message.messageId}>
                    {onSelectResult ? (
                      <DialogClose asChild>
                        <button
                          type="button"
                          onClick={() => onSelectResult(message.messageId)}
                          className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-colors"
                        >
                          {row}
                        </button>
                      </DialogClose>
                    ) : (
                      <div className="px-4 py-2.5">{row}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {hasNextPage && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full py-2 text-sm text-primary hover:bg-muted/60 rounded-lg transition-colors disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
